import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';

import PublicNavigation from '../../components/ui/PublicNavigation';
import UnifiedFooter from '../../components/ui/UnifiedFooter';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ContactSection from '../shipping-information/components/ContactSection';
import ChatWidget from '../../components/ui/ChatWidget';
import NewsletterBanner from '../../components/ui/NewsletterBanner';

const LogisticsDigitalCockpitArticle = () => {
  const [copiedLink, setCopiedLink] = useState(false);
  const [openFAQ, setOpenFAQ] = useState(null);

  const handleCopyLink = () => {
    navigator.clipboard?.writeText(window.location?.href);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const tableOfContents = [
  { id: 'corridor', title: 'Le corridor logistique Paris – Abidjan : un axe stratégique' },
  { id: 'limites', title: 'Pourquoi le modèle logistique traditionnel atteint ses limites' },
  { id: 'espace-pro', title: "L\'espace professionnel ADT Express : un centre de pilotage logistique" },
  { id: 'barrique', title: 'Envoyer une barrique ou un conteneur vers Abidjan : une gestion simplifiée' },
  { id: 'securite', title: 'Sécurité, transparence et performance' },
  { id: 'cible', title: 'À qui s\'adresse cette solution ?' },
  { id: 'tarifs', title: 'Tarifs et maîtrise budgétaire' },
  { id: 'faq', title: 'Questions fréquentes' }];


  const faqItems = [
  {
    question: "Quel est le tarif pour un envoi colis vers la Côte d'Ivoire ?",
    answer: "Les tarifs pour un envoi colis Côte d'Ivoire varient selon le volume et le type d'expédition. Pour une barrique standard (200 litres), comptez entre 150€ et 180€ tout compris, incluant le transport maritime Paris Abidjan, le dédouanement et la livraison à domicile. Pour un conteneur complet, les tarifs démarrent à partir de 2 500€. L'espace professionnel ADT Express vous permet de consulter vos tarifs personnalisés en temps réel et d'optimiser vos coûts grâce à l'historique de vos expéditions. Demandez un devis personnalisé pour obtenir une estimation précise adaptée à vos besoins."
  },
  {
    question: "Comment envoyer une barrique vers Abidjan ?",
    answer: "Envoyer une barrique vers Abidjan avec ADT Express est simple et entièrement digitalisé. Commandez votre barrique vide ou apportez la vôtre déjà remplie. Nous organisons la collecte à votre domicile en France, le stockage sécurisé dans notre entrepôt, puis le départ maritime vers Abidjan. Votre espace professionnel vous permet de suivre chaque étape en temps réel : collecte, entreposage, départ maritime, arrivée au port, dédouanement et livraison finale. Vous recevez des notifications automatiques à chaque changement de statut. Le processus complet prend généralement 4 à 6 semaines selon les départs maritimes."
  },
  {
    question: "Combien coûte un conteneur France Côte d\'Ivoire ?",
    answer: "Le prix d'un conteneur France Côte d'Ivoire dépend du type de conteneur et du volume expédié. Un conteneur 20 pieds en groupage maritime coûte entre 2 500€ et 4 000€, tandis qu'un conteneur 40 pieds complet peut atteindre 5 500€ à 7 000€. Ces tarifs incluent le fret maritime Paris Abidjan, les frais portuaires, le dédouanement et la livraison à Abidjan. Grâce à votre espace professionnel, vous pouvez analyser vos coûts logistiques sur l'année, identifier les opportunités d'optimisation et bénéficier de tarifs préférentiels selon vos volumes. Contactez notre équipe pour une étude tarifaire personnalisée."
  },
  {
    question: "Peut-on suivre un colis maritime en temps réel ?",
    answer: "Oui, absolument. ADT Express met à disposition un espace professionnel sécurisé permettant le suivi en temps réel de tous vos envois maritimes vers la Côte d'Ivoire. Vous visualisez instantanément le statut de chaque expédition : en préparation, collectée, en entrepôt France, embarquée, en transit maritime, arrivée à Abidjan, en dédouanement, en cours de livraison ou livrée. Chaque changement de statut déclenche une notification automatique par email et SMS. Vous accédez également aux photos de vos colis, aux documents douaniers et aux preuves de livraison, le tout centralisé dans votre tableau de bord accessible 24h/24."
  },
  {
    question: "Comment récupérer la TVA avec le certificat EX1 ?",
    answer: "Le certificat EX1 est un document douanier essentiel pour récupérer la TVA sur vos exportations vers la Côte d'Ivoire. ADT Express gère l'ensemble du processus pour vous : nous obtenons le certificat EX1 auprès des douanes françaises lors de l'export, puis le mettons à disposition dans votre espace professionnel sous format PDF téléchargeable. Vous pouvez ainsi le transmettre directement à votre comptable pour votre déclaration de TVA. Tous vos certificats EX1 sont archivés et accessibles à tout moment dans la section Documents de votre tableau de bord. Ce service est inclus dans nos prestations et vous fait gagner un temps précieux dans vos démarches administratives."
  }];


  const relatedArticles = [
  {
    title: "Les 10 choses à savoir avant d\'envoyer un colis en Côte d\'Ivoire",
    category: "Guide Pratique",
    date: "5 Février 2026",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1f2a6f50a-1772217450034.png",
    excerpt: "Découvrez les points essentiels à connaître pour un envoi réussi."
  },
  {
    title: "Barrique noire vs barrique bleue : laquelle choisir ?",
    category: "Guide Pratique",
    date: "28 Janvier 2026",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1284b4e14-1770849477282.png",
    excerpt: "Comparatif détaillé pour vous aider à choisir la barrique adaptée."
  },
  {
    title: "Transport maritime vers Abidjan : comment envoyer vos affaires depuis Paris ?",
    category: "Transport Maritime",
    date: "11 Février 2026",
    image: "https://images.unsplash.com/photo-1431610792557-0e2deb7199dd",
    excerpt: "Guide complet sur le transport maritime de Paris à Abidjan."
  }];


  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Logistique France – Côte d\'Ivoire : piloter vos expéditions depuis un véritable cockpit digital",
    "description": "Découvrez comment ADT Express transforme la logistique France – Côte d'Ivoire avec une plateforme digitale professionnelle. Suivi temps réel, gestion documentaire, certificats EX1, analyse des coûts.",
    "image": "https://img.rocket.new/generatedImages/rocket_gen_img_1ab98dbc9-1771190424374.png",
    "author": {
      "@type": "Organization",
      "name": "ADT Express"
    },
    "publisher": {
      "@type": "Organization",
      "name": "ADT Express",
      "logo": {
        "@type": "ImageObject",
        "url": "https://www.adtexpress.fr/logo.png"
      }
    },
    "datePublished": "2026-02-15",
    "dateModified": "2026-02-15",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://www.adtexpress.fr/blog/logistique-france-cote-ivoire-cockpit-digital"
    }
  };

  return (
    <>
      <Helmet>
        <title>Logistique France – Côte d'Ivoire : piloter vos expéditions depuis un véritable cockpit digital | ADT Express</title>
        <meta name="description" content="Découvrez comment ADT Express transforme la logistique France – Côte d'Ivoire avec une plateforme digitale professionnelle. Suivi temps réel, gestion documentaire, certificats EX1, analyse des coûts." />
        <meta name="keywords" content="envoi colis Côte d'Ivoire, fret maritime Paris Abidjan, envoyer barrique Côte d'Ivoire, barrique diaspora ivoirienne, envoi conteneur Abidjan, tarif envoi Côte d'Ivoire, groupage maritime France Côte d'Ivoire, fût maritime Paris Abidjan, coût barrique Abidjan, prix conteneur France Côte d'Ivoire" />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>
      <div className="min-h-screen bg-white">
        <PublicNavigation />
        <NewsletterBanner />
        <ChatWidget />

        {/* Breadcrumb */}
        <div className="bg-gray-50 border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <nav className="flex items-center gap-2 text-sm text-gray-600">
              <Link to="/" className="hover:text-green-700">Accueil</Link>
              <Icon name="ChevronRight" className="w-4 h-4" />
              <Link to="/maritime-transport-blog-hub" className="hover:text-green-700">Blog</Link>
              <Icon name="ChevronRight" className="w-4 h-4" />
              <span className="text-gray-900 font-medium">Logistique Digitale</span>
            </nav>
          </div>
        </div>

        {/* Article Header */}
        <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-green-900 text-white py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3 mb-6">
              <span className="px-4 py-1.5 bg-green-500/20 border border-green-400/30 rounded-full text-sm font-medium text-green-300">
                Logistique Digitale
              </span>
              <span className="text-gray-300 text-sm">15 Février 2026</span>
              <span className="text-gray-400">•</span>
              <span className="text-gray-300 text-sm flex items-center gap-1">
                <Icon name="Clock" className="w-4 h-4" />
                8 min de lecture
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
              Logistique France – Côte d'Ivoire : piloter vos expéditions depuis un véritable cockpit digital
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              ADT Express ne se positionne pas uniquement comme transporteur, mais comme plateforme logistique digitale dédiée au corridor France – Abidjan.
            </p>
          </div>
        </div>

        {/* Article Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            {/* Main Content */}
            <article className="lg:col-span-8">
              {/* Hero Image */}
              <div className="mb-12 rounded-2xl overflow-hidden shadow-xl">
                <img
                  src="https://img.rocket.new/generatedImages/rocket_gen_img_1ab98dbc9-1771190424374.png"
                  alt="Plateforme logistique digitale pour le corridor France - Côte d'Ivoire avec suivi en temps réel des expéditions maritimes"
                  className="w-full h-[400px] object-cover" />
                
              </div>

              {/* Introduction */}
              <div className="prose prose-lg max-w-none mb-12">
                <p className="text-lg text-gray-700 leading-relaxed">
                  Les exportations vers la Côte d'Ivoire représentent un enjeu stratégique majeur pour de nombreuses entreprises françaises. Entre la complexité des formalités douanières, la gestion des documents d'export, le suivi des expéditions maritimes et la coordination avec les destinataires à Abidjan, les défis logistiques sont nombreux. Pour les professionnels qui expédient régulièrement des barriques, des conteneurs ou des colis vers la Côte d'Ivoire, la nécessité d'une visibilité complète et d'une centralisation des informations n'a jamais été aussi cruciale.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed">
                  Que vous soyez commerçant exportateur, membre de la diaspora ivoirienne gérant des envois familiaux professionnels, ou entreprise industrielle, la maîtrise de votre chaîne logistique France – Abidjan détermine votre efficacité opérationnelle. L'<strong>envoi colis Côte d'Ivoire</strong>, le <strong>fret maritime Paris Abidjan</strong> ou encore l'<strong>envoi de barrique Côte d'Ivoire</strong> nécessitent une coordination précise et une traçabilité sans faille.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed">
                  Face à ces enjeux, ADT Express ne se positionne pas uniquement comme transporteur, mais comme <strong>plateforme logistique digitale</strong> dédiée au corridor France – Abidjan. Notre espace professionnel transforme la gestion de vos expéditions en un véritable centre de pilotage, accessible 24h/24, où chaque information critique est centralisée, sécurisée et exploitable.
                </p>
              </div>

              {/* Section 1 */}
              <section id="corridor" className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                  <span className="w-2 h-8 bg-green-500 rounded"></span>
                  Le corridor logistique Paris – Abidjan : un axe stratégique
                </h2>
                <div className="prose prose-lg max-w-none">
                  <p className="text-gray-700 leading-relaxed">
                    Le corridor France – Côte d'Ivoire connaît une croissance soutenue depuis plusieurs années. Cette dynamique s'explique par plusieurs facteurs structurels : la présence d'une importante diaspora ivoirienne en France, l'intensification des échanges commerciaux entre les deux pays, et le développement économique continu de la Côte d'Ivoire qui attire de plus en plus d'exportateurs français.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    Pour la <strong>barrique diaspora ivoirienne</strong>, ce corridor représente le lien vital avec les familles restées au pays. Chaque année, des milliers de barriques transitent de Paris vers Abidjan, chargées de produits alimentaires, de vêtements, d'électroménager et de biens personnels. Parallèlement, les PME exportatrices utilisent massivement l'<strong>envoi conteneur Abidjan</strong> pour acheminer leurs marchandises : produits cosmétiques, pièces détachées automobiles, équipements industriels, matériaux de construction.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    Dans ce contexte de flux croissants, la fiabilité logistique devient un avantage concurrentiel décisif. Les entreprises qui maîtrisent leur chaîne d'approvisionnement vers la Côte d'Ivoire gagnent en réactivité, réduisent leurs coûts et améliorent leur service client. C'est précisément pour répondre à ces besoins que la transparence sur les <strong>tarifs envoi Côte d'Ivoire</strong> et la visibilité en temps réel deviennent des standards incontournables.
                  </p>
                </div>
              </section>

              {/* Section 2 */}
              <section id="limites" className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                  <span className="w-2 h-8 bg-green-500 rounded"></span>
                  Pourquoi le modèle logistique traditionnel atteint ses limites
                </h2>
                <div className="prose prose-lg max-w-none">
                  <p className="text-gray-700 leading-relaxed">
                    Le modèle logistique traditionnel repose sur une communication fragmentée : emails dispersés dans plusieurs boîtes de réception, documents PDF stockés localement, appels téléphoniques pour obtenir des mises à jour, absence de vision consolidée sur l'ensemble des expéditions en cours. Cette approche génère des inefficacités coûteuses.
                  </p>
                  <div className="bg-red-50 border-l-4 border-red-500 p-6 my-6 rounded-r-lg">
                    <h3 className="text-xl font-semibold text-red-900 mb-3">Les conséquences opérationnelles :</h3>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start gap-2">
                        <Icon name="AlertCircle" className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                        <span><strong>Perte de temps :</strong> Recherche manuelle d'informations, relances multiples, absence de centralisation</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Icon name="AlertCircle" className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                        <span><strong>Surcoûts cachés :</strong> Impossibilité d'analyser les coûts logistiques, pas de vision annuelle, difficultés à négocier</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Icon name="AlertCircle" className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                        <span><strong>Risques douaniers :</strong> Documents manquants, certificats EX1 non récupérés, perte de traçabilité</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Icon name="AlertCircle" className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                        <span><strong>Manque de visibilité :</strong> Aucun suivi temps réel, statut des expéditions inconnu, stress client</span>
                      </li>
                    </ul>
                  </div>
                  <p className="text-gray-700 leading-relaxed">
                    Pour les professionnels qui expédient régulièrement vers la Côte d'Ivoire, ces limites se traduisent par une charge administrative lourde, une difficulté à anticiper les arrivées de marchandises, et une impossibilité à optimiser les coûts logistiques. La récupération des certificats EX1 pour la TVA devient un parcours du combattant, les factures s'accumulent sans vision consolidée, et le suivi des expéditions nécessite des relances constantes.
                  </p>
                </div>
              </section>

              {/* Section 3 */}
              <section id="espace-pro" className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                  <span className="w-2 h-8 bg-green-500 rounded"></span>
                  L'espace professionnel ADT Express : un centre de pilotage logistique
                </h2>
                <div className="prose prose-lg max-w-none">
                  <p className="text-gray-700 leading-relaxed mb-8">
                    Face à ces défis, ADT Express a développé une plateforme digitale professionnelle qui centralise l'intégralité de votre activité logistique France – Côte d'Ivoire. Accessible 24h/24 depuis n'importe quel appareil, cet espace transforme la gestion de vos expéditions en un processus fluide, transparent et maîtrisé.
                  </p>

                  {/* Sub-sections with H3 */}
                  <div className="space-y-8">
                    <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-xl border border-green-200">
                      <h3 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <Icon name="MapPin" className="w-6 h-6 text-green-600" />
                        Suivi en temps réel des expéditions
                      </h3>
                      <p className="text-gray-700 leading-relaxed">
                        Visualisez instantanément le statut de chaque expédition : collecte programmée, en entrepôt France, embarquée, en transit maritime, arrivée à Abidjan, en dédouanement, en cours de livraison. Chaque changement de statut déclenche une notification automatique. Le tracking du corridor France – Côte d'Ivoire devient aussi simple que la consultation d'un tableau de bord automobile.
                      </p>
                    </div>

                    <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-6 rounded-xl border border-blue-200">
                      <h3 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <Icon name="FileText" className="w-6 h-6 text-blue-600" />
                        Centralisation des factures et documents
                      </h3>
                      <p className="text-gray-700 leading-relaxed">
                        Téléchargez vos factures en un clic, consultez l'historique complet de vos paiements, accédez aux documents douaniers (DAU, manifeste, connaissement). Fini les recherches dans les emails : tout est archivé, daté et accessible instantanément. Votre comptabilité gagne en efficacité, vos audits deviennent simples.
                      </p>
                    </div>

                    <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-6 rounded-xl border border-purple-200">
                      <h3 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <Icon name="Award" className="w-6 h-6 text-purple-600" />
                        Gestion des certificats EX1
                      </h3>
                      <p className="text-gray-700 leading-relaxed">
                        Récupérez automatiquement vos certificats EX1 pour la récupération de TVA. Chaque export génère son certificat, disponible en téléchargement PDF dans votre espace. Le processus de récupération de TVA, souvent complexe et chronophage, devient un simple clic. Votre expert-comptable accède directement aux documents nécessaires.
                      </p>
                    </div>

                    <div className="bg-gradient-to-br from-orange-50 to-amber-50 p-6 rounded-xl border border-orange-200">
                      <h3 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <Icon name="TrendingUp" className="w-6 h-6 text-orange-600" />
                        Analyse des coûts logistiques
                      </h3>
                      <p className="text-gray-700 leading-relaxed">
                        Visualisez vos dépenses logistiques par mois, par type d'expédition, par volume. Identifiez les opportunités d'optimisation : regroupement de barriques, passage au conteneur complet, négociation de tarifs préférentiels. La vision annuelle de vos coûts vous permet de budgétiser précisément et de négocier en position de force.
                      </p>
                    </div>

                    <div className="bg-gradient-to-br from-slate-50 to-gray-50 p-6 rounded-xl border border-slate-200">
                      <h3 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <Icon name="Archive" className="w-6 h-6 text-slate-600" />
                        Historique complet des opérations
                      </h3>
                      <p className="text-gray-700 leading-relaxed">
                        Archivage sécurisé de toutes vos expéditions depuis le premier jour. Recherchez une expédition par référence, par date, par destinataire. Consultez les photos de vos colis, les preuves de livraison, les échanges avec notre équipe. La traçabilité totale garantit votre conformité réglementière et facilite la gestion des litiges éventuels.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Section 4 */}
              <section id="barrique" className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                  <span className="w-2 h-8 bg-green-500 rounded"></span>
                  Envoyer une barrique ou un conteneur vers Abidjan : une gestion simplifiée
                </h2>
                <div className="prose prose-lg max-w-none">
                  <p className="text-gray-700 leading-relaxed">
                    Que vous souhaitiez <strong>envoyer une barrique Côte d'Ivoire</strong> pour votre famille ou expédier un <strong>fût maritime Paris Abidjan</strong> dans le cadre de votre activité commerciale, le processus reste identique et entièrement digitalisé. L'<strong>envoi conteneur Abidjan</strong> ou le <strong>groupage maritime France Côte d'Ivoire</strong> suivent le même parcours transparent.
                  </p>

                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-8 rounded-2xl my-8 border border-green-200">
                    <h3 className="text-xl font-semibold text-gray-900 mb-6">Le parcours de votre expédition :</h3>
                    <div className="space-y-4">
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-full bg-green-500 text-white flex items-center justify-center font-bold flex-shrink-0">1</div>
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-1">Collecte en France</h4>
                          <p className="text-gray-700">Prise en charge à votre domicile ou en point relais, enregistrement immédiat dans votre espace pro</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-full bg-green-500 text-white flex items-center justify-center font-bold flex-shrink-0">2</div>
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-1">Stockage sécurisé</h4>
                          <p className="text-gray-700">Entreposage dans notre plateforme logistique, photos disponibles dans votre tableau de bord</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-full bg-green-500 text-white flex items-center justify-center font-bold flex-shrink-0">3</div>
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-1">Départ maritime</h4>
                          <p className="text-gray-700">Embarquement dans le conteneur, notification automatique avec date de départ et ETA Abidjan</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-full bg-green-500 text-white flex items-center justify-center font-bold flex-shrink-0">4</div>
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-1">Dédouanement</h4>
                          <p className="text-gray-700">Gestion complète des formalités douanières, certificat EX1 disponible en téléchargement</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-full bg-green-500 text-white flex items-center justify-center font-bold flex-shrink-0">5</div>
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-1">Livraison Abidjan</h4>
                          <p className="text-gray-700">Livraison à domicile ou en agence, preuve de livraison avec signature disponible dans votre espace</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <p className="text-gray-700 leading-relaxed">
                    Chaque étape est tracée, documentée et accessible depuis votre espace professionnel. Vous ne subissez plus votre logistique : vous la pilotez.
                  </p>
                </div>
              </section>

              {/* Section 5 */}
              <section id="securite" className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                  <span className="w-2 h-8 bg-green-500 rounded"></span>
                  Sécurité, transparence et performance
                </h2>
                <div className="prose prose-lg max-w-none">
                  <p className="text-gray-700 leading-relaxed mb-6">
                    La plateforme ADT Express repose sur des standards de sécurité bancaires. Vos données sont chiffrées, vos documents archivés de manière conforme au RGPD, vos accès protégés par authentification multi-facteurs.
                  </p>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="bg-white p-6 rounded-xl border-2 border-gray-200 hover:border-green-500 transition-colors">
                      <Icon name="Shield" className="w-10 h-10 text-green-600 mb-3" />
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">Environnement sécurisé</h3>
                      <p className="text-gray-700">Chiffrement SSL, hébergement certifié, sauvegardes quotidiennes</p>
                    </div>
                    <div className="bg-white p-6 rounded-xl border-2 border-gray-200 hover:border-green-500 transition-colors">
                      <Icon name="Database" className="w-10 h-10 text-blue-600 mb-3" />
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">Données centralisées</h3>
                      <p className="text-gray-700">Toutes vos informations logistiques en un seul endroit accessible</p>
                    </div>
                    <div className="bg-white p-6 rounded-xl border-2 border-gray-200 hover:border-green-500 transition-colors">
                      <Icon name="Clock" className="w-10 h-10 text-purple-600 mb-3" />
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">Accès 24/24</h3>
                      <p className="text-gray-700">Consultez vos expéditions à tout moment, depuis n'importe quel appareil</p>
                    </div>
                    <div className="bg-white p-6 rounded-xl border-2 border-gray-200 hover:border-green-500 transition-colors">
                      <Icon name="Bell" className="w-10 h-10 text-orange-600 mb-3" />
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">Notifications intelligentes</h3>
                      <p className="text-gray-700">Alertes automatiques par email et SMS à chaque changement de statut</p>
                    </div>
                  </div>
                  <p className="text-gray-700 leading-relaxed mt-6">
                    Au-delà de la technologie, vous bénéficiez d'un <strong>interlocuteur dédié</strong> qui connaît votre activité, anticipe vos besoins et vous accompagne dans l'optimisation de votre chaîne logistique France – Côte d'Ivoire.
                  </p>
                </div>
              </section>

              {/* Section 6 */}
              <section id="cible" className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                  <span className="w-2 h-8 bg-green-500 rounded"></span>
                  À qui s'adresse cette solution ?
                </h2>
                <div className="prose prose-lg max-w-none">
                  <p className="text-gray-700 leading-relaxed mb-6">
                    L'espace professionnel ADT Express a été conçu pour répondre aux besoins spécifiques de plusieurs profils d'utilisateurs :
                  </p>
                  <div className="space-y-4">
                    <div className="flex items-start gap-4 bg-gray-50 p-5 rounded-lg">
                      <Icon name="Store" className="w-6 h-6 text-green-600 mt-1 flex-shrink-0" />
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-1">Commerçants exportateurs</h3>
                        <p className="text-gray-700">Boutiques en ligne, grossistes, détaillants expédiant régulièrement vers la Côte d'Ivoire</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4 bg-gray-50 p-5 rounded-lg">
                      <Icon name="Users" className="w-6 h-6 text-blue-600 mt-1 flex-shrink-0" />
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-1">Associations</h3>
                        <p className="text-gray-700">Organisations humanitaires, associations culturelles, ONG gérant des envois groupés</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4 bg-gray-50 p-5 rounded-lg">
                      <Icon name="Building" className="w-6 h-6 text-purple-600 mt-1 flex-shrink-0" />
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-1">Entreprises industrielles</h3>
                        <p className="text-gray-700">PME exportant équipements, pièces détachées, matériaux vers l'Afrique de l'Ouest</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4 bg-gray-50 p-5 rounded-lg">
                      <Icon name="Globe" className="w-6 h-6 text-orange-600 mt-1 flex-shrink-0" />
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-1">Diaspora ivoirienne professionnelle</h3>
                        <p className="text-gray-700">Professionnels gérant des envois familiaux réguliers nécessitant traçabilité et fiabilité</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4 bg-gray-50 p-5 rounded-lg">
                      <Icon name="Package" className="w-6 h-6 text-red-600 mt-1 flex-shrink-0" />
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-1">Importateurs Abidjan</h3>
                        <p className="text-gray-700">Entreprises ivoiriennes important depuis la France avec besoin de visibilité totale</p>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Section 7 */}
              <section id="tarifs" className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                  <span className="w-2 h-8 bg-green-500 rounded"></span>
                  Tarifs et maîtrise budgétaire
                </h2>
                <div className="prose prose-lg max-w-none">
                  <p className="text-gray-700 leading-relaxed">
                    La transparence tarifaire est au cœur de notre approche. Que vous recherchiez le <strong>tarif envoi Côte d'Ivoire</strong> pour une barrique, le <strong>coût barrique Abidjan</strong> tout compris, ou le <strong>prix conteneur France Côte d'Ivoire</strong>, votre espace professionnel vous donne accès à une grille tarifaire claire et personnalisée.
                  </p>
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-8 rounded-2xl my-8 border border-blue-200">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">La visibilité digitale au service de votre budget</h3>
                    <ul className="space-y-3 text-gray-700">
                      <li className="flex items-start gap-3">
                        <Icon name="CheckCircle" className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                        <span>Consultez vos tarifs personnalisés en fonction de vos volumes annuels</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <Icon name="CheckCircle" className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                        <span>Analysez vos dépenses logistiques mois par mois pour anticiper vos budgets</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <Icon name="CheckCircle" className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                        <span>Identifiez les opportunités d'optimisation : regroupement, conteneur complet, planning</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <Icon name="CheckCircle" className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                        <span>Téléchargez vos factures et justificatifs pour votre comptabilité en un clic</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <Icon name="CheckCircle" className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                        <span>Récupérez automatiquement vos certificats EX1 pour la récupération de TVA</span>
                      </li>
                    </ul>
                  </div>
                  <p className="text-gray-700 leading-relaxed">
                    La maîtrise budgétaire ne se limite pas à connaître les tarifs : elle nécessite une vision consolidée, une capacité d'analyse et des outils de pilotage. C'est exactement ce que propose l'espace professionnel ADT Express.
                  </p>
                </div>
              </section>

              {/* Conclusion */}
              <section className="mb-12">
                <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-green-900 text-white p-10 rounded-2xl">
                  <h2 className="text-3xl font-bold mb-6">
                    La logistique ne doit plus être subie, elle doit être pilotée
                  </h2>
                  <p className="text-lg text-gray-200 leading-relaxed mb-6">
                    ADT Express propose une infrastructure logistique digitale pensée pour les professionnels opérant entre la France et la Côte d'Ivoire. Notre plateforme transforme la complexité logistique en simplicité opérationnelle, la dispersion des informations en centralisation intelligente, et l'incertitude en visibilité totale.
                  </p>
                  <p className="text-lg text-gray-200 leading-relaxed mb-8">
                    Que vous expédiez des barriques pour votre famille, des conteneurs pour votre commerce, ou des équipements industriels pour vos clients, vous méritez un partenaire logistique qui vous donne les moyens de piloter vos flux avec précision, sécurité et performance.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4">
                    <Link to="/professional-client-register">
                      <Button className="bg-green-500 hover:bg-green-600 text-white px-8 py-3 rounded-lg font-semibold flex items-center gap-2 w-full sm:w-auto justify-center">
                        <Icon name="UserPlus" className="w-5 h-5" />
                        Créer un espace professionnel
                      </Button>
                    </Link>
                    <Link to="/commander">
                      <Button className="bg-white text-slate-900 hover:bg-gray-100 px-8 py-3 rounded-lg font-semibold flex items-center gap-2 w-full sm:w-auto justify-center">
                        <Icon name="FileText" className="w-5 h-5" />
                        Demander un devis France – Abidjan
                      </Button>
                    </Link>
                  </div>
                </div>
              </section>

              {/* FAQ Section */}
              <section id="faq" className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-8 flex items-center gap-3">
                  <span className="w-2 h-8 bg-green-500 rounded"></span>
                  Questions fréquentes
                </h2>
                <div className="space-y-4">
                  {faqItems?.map((item, index) =>
                  <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                      <button
                      onClick={() => setOpenFAQ(openFAQ === index ? null : index)}
                      className="w-full px-6 py-4 text-left bg-white hover:bg-gray-50 transition-colors flex items-center justify-between gap-4">
                      
                        <span className="font-semibold text-gray-900 text-lg">{item?.question}</span>
                        <Icon
                        name={openFAQ === index ? "ChevronUp" : "ChevronDown"}
                        className="w-5 h-5 text-gray-500 flex-shrink-0" />
                      
                      </button>
                      {openFAQ === index &&
                    <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                          <p className="text-gray-700 leading-relaxed">{item?.answer}</p>
                        </div>
                    }
                    </div>
                  )}
                </div>
              </section>

              {/* Share Section */}
              <div className="border-t border-gray-200 pt-8">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-3">
                    <span className="text-gray-600 font-medium">Partager cet article :</span>
                    <div className="flex gap-2">
                      <button className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                        <Icon name="Facebook" className="w-5 h-5" />
                      </button>
                      <button className="p-2 bg-sky-500 text-white rounded-lg hover:bg-sky-600 transition-colors">
                        <Icon name="Twitter" className="w-5 h-5" />
                      </button>
                      <button className="p-2 bg-blue-700 text-white rounded-lg hover:bg-blue-800 transition-colors">
                        <Icon name="Linkedin" className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                  <button
                    onClick={handleCopyLink}
                    className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">
                    
                    <Icon name={copiedLink ? "Check" : "Link"} className="w-5 h-5 text-gray-700" />
                    <span className="text-gray-700 font-medium">
                      {copiedLink ? "Lien copié !" : "Copier le lien"}
                    </span>
                  </button>
                </div>
              </div>
            </article>

            {/* Sidebar */}
            <aside className="lg:col-span-4">
              {/* Table of Contents */}
              <div className="bg-gray-50 rounded-xl p-6 mb-8 sticky top-24">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Icon name="List" className="w-5 h-5 text-green-600" />
                  Sommaire
                </h3>
                <nav className="space-y-2">
                  {tableOfContents?.map((item, index) =>
                  <a
                    key={index}
                    href={`#${item?.id}`}
                    className="block text-sm text-gray-700 hover:text-green-600 hover:translate-x-1 transition-all py-2 border-l-2 border-transparent hover:border-green-500 pl-3">
                    
                      {item?.title}
                    </a>
                  )}
                </nav>
              </div>

              {/* CTA Card */}
              <div className="bg-gradient-to-br from-green-600 to-emerald-700 rounded-xl p-6 mb-8">
                <Icon name="Rocket" className="w-12 h-12 mb-4 text-white" />
                <h3 className="text-xl font-bold mb-3 text-white">Prêt à digitaliser votre logistique ?</h3>
                <p className="text-green-100 mb-6 text-sm">
                  Créez votre espace professionnel et pilotez vos expéditions France – Côte d'Ivoire en toute sérénité.
                </p>
                <Link to="/professional-client-register">
                  <Button className="w-full bg-white text-green-700 hover:bg-gray-100 font-semibold py-3 rounded-lg">
                    Créer mon compte
                  </Button>
                </Link>
              </div>
            </aside>
          </div>

          {/* Related Articles */}
          <div className="mt-16 border-t border-gray-200 pt-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Articles recommandés</h2>
            <div className="grid md:grid-cols-3 gap-8">
              {relatedArticles?.map((article, index) =>
              <Link
                key={index}
                to="/maritime-transport-blog-article"
                className="group bg-white rounded-xl overflow-hidden border border-gray-200 hover:shadow-xl transition-all duration-300">
                
                  <div className="relative h-48 overflow-hidden">
                    <img
                    src={article?.image}
                    alt={article?.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300" />
                  
                    <div className="absolute top-4 left-4">
                      <span className="px-3 py-1 bg-white/90 backdrop-blur-sm rounded-full text-xs font-medium text-gray-900">
                        {article?.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <p className="text-sm text-gray-500 mb-2">{article?.date}</p>
                    <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-green-600 transition-colors line-clamp-2">
                      {article?.title}
                    </h3>
                    <p className="text-gray-600 text-sm line-clamp-2">{article?.excerpt}</p>
                  </div>
                </Link>
              )}
            </div>
          </div>
        </div>

        <ContactSection />
        <UnifiedFooter />
      </div>
    </>);

};

export default LogisticsDigitalCockpitArticle;